// Pure zoom/pan/scale math for the annotatable time-chart. No React, no DOM —
// every function is a plain input→output transform so the tricky bits
// (finger-anchored pinch, clamping) are unit-tested in isolation
// (chartMath.vitest.ts). All times are epoch ms; all pixel coords are relative
// to the plot area's left edge (0 = left, plotW = right).

import type { Domain } from './types';

/** Don't zoom tighter than 5 minutes — matches DayChartLightbox.MIN_SPAN_MS. */
export const MIN_SPAN_MS = 5 * 60 * 1000;

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Time (ms) → pixel x within [0, plotW] for the given domain. Not clamped:
 *  callers that need on-screen coords clamp themselves. */
export function timeToPx(t: number, domain: Domain, plotW: number): number {
  const span = domain[1] - domain[0];
  if (span <= 0) return 0;
  return ((t - domain[0]) / span) * plotW;
}

/** Pixel x within [0, plotW] → time (ms) for the given domain. */
export function pxToTime(px: number, domain: Domain, plotW: number): number {
  if (plotW <= 0) return domain[0];
  return domain[0] + (px / plotW) * (domain[1] - domain[0]);
}

/** Clamp a candidate domain so it (a) never spans wider than `full`, (b) never
 *  spans tighter than MIN_SPAN_MS, and (c) stays inside `full` (pan back into
 *  range rather than revealing empty gutter). Preserves the requested span
 *  where possible, shifting the window to fit. */
export function clampDomain(candidate: Domain, full: Domain): Domain {
  const fullSpan = full[1] - full[0];
  let span = candidate[1] - candidate[0];
  // Span bounds: never wider than the whole day, never tighter than the floor
  // (but if the whole day is itself narrower than the floor, allow it).
  span = clamp(span, Math.min(MIN_SPAN_MS, fullSpan), fullSpan);
  let start = candidate[0];
  // Shift the window so [start, start+span] sits inside `full`.
  if (start < full[0]) start = full[0];
  if (start + span > full[1]) start = full[1] - span;
  return [start, start + span];
}

/**
 * Finger-anchored pinch zoom. At gesture start we record the two data times
 * under the two fingers (`anchorA`, `anchorB`). As the fingers move to new
 * pixel positions (`pxA`, `pxB`), we solve for the domain that keeps each
 * anchor time exactly under its finger — so the axis tracks the fingers 1:1,
 * never faster nor slower (Alexey's explicit requirement).
 *
 *   px = (t - d0) * s,  where s = plotW / (d1 - d0)   [px per ms]
 *   pxA = (anchorA - d0) * s
 *   pxB = (anchorB - d0) * s
 *   ⟹ s  = (pxA - pxB) / (anchorA - anchorB)
 *     d0 = anchorA - pxA / s
 *     d1 = d0 + plotW / s
 *
 * Returns null when the gesture is degenerate (fingers coincident in pixels or
 * anchors coincident in time) — caller keeps the previous domain.
 */
export function pinchDomain(
  anchorA: number,
  anchorB: number,
  pxA: number,
  pxB: number,
  plotW: number,
): Domain | null {
  const dPx = pxA - pxB;
  const dT = anchorA - anchorB;
  if (dT === 0 || dPx === 0 || plotW <= 0) return null;
  const s = dPx / dT; // px per ms; sign handled by both diffs
  if (!Number.isFinite(s) || s === 0) return null;
  const d0 = anchorA - pxA / s;
  const d1 = d0 + plotW / s;
  if (!Number.isFinite(d0) || !Number.isFinite(d1) || d1 <= d0) return null;
  return [d0, d1];
}

/**
 * Wheel / double-finger-tap zoom anchored at a single pixel (`pivotPx`): scale
 * the span by `factor` (<1 zooms in, >1 zooms out) while keeping the time under
 * the pivot fixed on screen.
 */
export function zoomAtPixel(
  domain: Domain,
  pivotPx: number,
  factor: number,
  plotW: number,
): Domain {
  const pivotT = pxToTime(pivotPx, domain, plotW);
  const d0 = pivotT - (pivotT - domain[0]) * factor;
  const d1 = pivotT + (domain[1] - pivotT) * factor;
  return [d0, d1];
}

/**
 * Pan the domain by a pixel delta (drag). Positive `dPx` drags content right,
 * i.e. shifts the domain earlier (toward smaller t), like grabbing the chart.
 */
export function panByPixels(domain: Domain, dPx: number, plotW: number): Domain {
  if (plotW <= 0) return domain;
  const dt = (dPx / plotW) * (domain[1] - domain[0]);
  return [domain[0] - dt, domain[1] - dt];
}

/** Marquee-select [pxA, pxB] → the time domain they enclose (order-agnostic). */
export function marqueeDomain(
  pxA: number,
  pxB: number,
  domain: Domain,
  plotW: number,
): Domain {
  const tA = pxToTime(pxA, domain, plotW);
  const tB = pxToTime(pxB, domain, plotW);
  return [Math.min(tA, tB), Math.max(tA, tB)];
}

/** Linearly interpolate the series value at time `t`. Points must be sorted by
 *  t. Returns null when `t` is outside the series or the series is empty — the
 *  editor decides what a null pre-fill means. Used to pre-compute annotation
 *  values on create/drag (E5). */
export function valueAt(points: { t: number; v: number }[], t: number): number | null {
  if (points.length === 0) return null;
  if (t <= points[0].t) return points[0].t === t ? points[0].v : null;
  if (t >= points[points.length - 1].t) {
    return points[points.length - 1].t === t ? points[points.length - 1].v : null;
  }
  // Binary search for the bracketing pair.
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].t <= t) lo = mid;
    else hi = mid;
  }
  const a = points[lo];
  const b = points[hi];
  const span = b.t - a.t;
  if (span <= 0) return a.v;
  return a.v + ((t - a.t) / span) * (b.v - a.v);
}
